import type { GitCommit } from '@slate/core';

/**
 * Pure commit-graph lane layout.
 *
 * We compute lanes ourselves from each commit's parent hashes rather than
 * parsing `git log --graph` ASCII art. The input is the commit list exactly as
 * `git log` returns it (newest first); the output assigns every commit a lane
 * (column) and the rail segments to draw across its row, so a renderer can draw
 * its own dots and edges.
 *
 * The algorithm is a classic active-lane sweep, O(rows * lanes):
 *   - `activeLanes[i]` is the hash that lane `i` is currently waiting to reach
 *     (its next expected commit), or null when the lane is free.
 *   - `laneColor[i]` is a stable color id for the lane's whole lifetime; a fresh
 *     incrementing id is allocated every time a lane is opened, so a rail keeps
 *     one color from the commit that opens it to the commit that closes it.
 */

/** An edge segment passing through one row, from a top lane to a bottom lane. */
export interface GraphEdge {
  fromLane: number; // lane index at the TOP boundary of this row
  toLane: number; // lane index at the BOTTOM boundary of this row
  color: number; // stable color/lane id for consistent rail coloring
}

/** One laid-out commit row. */
export interface GraphRow {
  commit: GitCommit;
  lane: number; // column index where this commit's dot is drawn
  color: number; // color id for this commit's lane
  edges: GraphEdge[]; // rails to draw across this row (incoming-above -> outgoing-below)
  width: number; // total number of lanes occupied across this row (for layout width)
}

/** Leftmost free (null/undefined) lane, or the append index when all are taken. */
function firstFreeLane(lanes: (string | null)[]): number {
  for (let i = 0; i < lanes.length; i++) {
    if (lanes[i] == null) return i;
  }
  return lanes.length;
}

export function layoutCommits(commits: GitCommit[]): GraphRow[] {
  const rows: GraphRow[] = [];
  // Lane state carried between rows.
  const activeLanes: (string | null)[] = [];
  const laneColor: number[] = [];
  let nextColor = 0;

  for (const commit of commits) {
    // Snapshot the lanes at the TOP boundary of this row before any mutation.
    const top = activeLanes.slice();

    // 1. Lanes that were waiting for this commit. The leftmost becomes the
    //    commit's lane; any others are "closing" into it.
    const closers: number[] = [];
    for (let i = 0; i < top.length; i++) {
      if (top[i] === commit.hash) closers.push(i);
    }

    // 2. Resolve the commit's own lane (and color).
    let lane: number;
    if (closers.length > 0) {
      lane = closers[0];
    } else {
      // A tip / first row: open a fresh lane with a fresh color.
      lane = firstFreeLane(activeLanes);
      laneColor[lane] = nextColor++;
    }
    const color = laneColor[lane];

    // 3. Reassign expectations for this commit's parents. The first parent
    //    continues in this commit's lane; each additional parent opens a fresh
    //    lane. A root (no parents) frees its lane.
    const newParentLanes: number[] = [];
    if (commit.parents.length === 0) {
      activeLanes[lane] = null;
    } else {
      activeLanes[lane] = commit.parents[0];
      for (let k = 1; k < commit.parents.length; k++) {
        // Closers are still occupied here (freed in step 6), so an extra parent
        // never reuses a closing lane; it takes the leftmost free slot/appends.
        const slot = firstFreeLane(activeLanes);
        activeLanes[slot] = commit.parents[k];
        laneColor[slot] = nextColor++;
        newParentLanes.push(slot);
      }
    }

    // 4. Build the rails for this row.
    const edges: GraphEdge[] = [];
    // First parent continues straight down this commit's lane (roots have none).
    if (commit.parents.length > 0) {
      edges.push({ fromLane: lane, toLane: lane, color });
    }
    // Other closers fold into this commit's lane, keeping their own color.
    for (const cl of closers) {
      if (cl !== lane) edges.push({ fromLane: cl, toLane: lane, color: laneColor[cl] });
    }
    // Additional parents branch out of this commit's lane into their new lanes.
    for (const np of newParentLanes) {
      edges.push({ fromLane: lane, toLane: np, color: laneColor[np] });
    }
    // Every unrelated lane passes straight through at its own index.
    for (let i = 0; i < top.length; i++) {
      if (top[i] == null) continue;
      if (i === lane) continue; // this commit's lane, handled above
      if (top[i] === commit.hash) continue; // a closer, handled above
      edges.push({ fromLane: i, toLane: i, color: laneColor[i] });
    }

    // 5. Width = highest occupied lane (at top OR bottom, plus the dot) + 1,
    //    so trailing free lanes are trimmed and linear history stays width 1.
    const occupied = new Set<number>();
    for (let i = 0; i < top.length; i++) {
      if (top[i] != null) occupied.add(i);
    }
    for (let i = 0; i < activeLanes.length; i++) {
      if (activeLanes[i] != null) occupied.add(i);
    }
    occupied.add(lane);
    const width = Math.max(...occupied) + 1;

    // 6. Free the closing lanes now that their edges are recorded.
    for (const cl of closers) {
      if (cl !== lane) activeLanes[cl] = null;
    }

    rows.push({ commit, lane, color, edges, width });
  }

  return rows;
}

/**
 * The commit `hash` plus every ancestor reachable through `parents` that is
 * present in `commits` (an "isolate" of one tip's history).
 *
 * Walks the parent DAG breadth-first from `hash`, adding each visited hash to
 * the result. Cycles are guarded by skipping already-visited hashes, and a
 * parent hash that is not present in `commits` is simply not traversed (so the
 * isolate stays within the loaded window). Returns an empty set when `hash`
 * itself is not in `commits`.
 */
export function ancestorsOf(commits: GitCommit[], hash: string): Set<string> {
  const byHash = new Map<string, GitCommit>();
  for (const commit of commits) byHash.set(commit.hash, commit);

  const result = new Set<string>();
  if (!byHash.has(hash)) return result;

  const queue: string[] = [hash];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (result.has(current)) continue; // already visited; guards cycles
    result.add(current);
    const commit = byHash.get(current);
    if (!commit) continue; // a parent we recorded but never loaded
    for (const parent of commit.parents) {
      // Skip parents already visited or outside the loaded window.
      if (!result.has(parent) && byHash.has(parent)) queue.push(parent);
    }
  }

  return result;
}
